import { Duration, Instant, LocalDateTime } from "@js-joda/core";
import assertNever from "assert-never";
import React from "react";
import FirebaseAnalytics from "../FirebaseAnalytics";
import { Loading } from "../Loading";
import { getClockInStrategy, getClockOutStrategy } from "../clockInOutUtils";
import { formatPatientInitials } from "../formatUtils";
import locationUtils, {
  HAS_PROMPTED_WITH_LOCATION_ADDRESS,
  getDistanceInFeet,
  getGeoAndAddressByPosition,
} from "../locationUtils";
import { Geo, GeoAndAddress, VisitInstanceClockinClockoutRequestParams } from "../messages";
import { CaregiverId, VisitInstanceId } from "../messages/ObjectIds";
import {
  ClockInOutCallType,
  ClockedInStatus,
  ClockinClockoutCheckAdjustmentStatusResponse,
  VisitInstanceDetails,
} from "../messages/Visit";
import { removeLocalStorageItem } from "../storage_utils";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import PointsConfetti from "./CaregiverPoints/PointsConfetti";
import ContactSupportModal from "./ContactSupportModal";
import { ErrorModal, LoadingModal } from "./Generic/AppModal/AppModal";
import PromptAdjustVisitTimesModal, {
  PromptAdjustVisitTimesModalProps,
} from "./PromptAdjustVisitTimesModal/PromptAdjustVisitTimesModal";
import PromptCaregiverLocationTooFarModal, {
  PromptCaregiverLocationTooFarProps,
} from "./PromptCaregiverLocationTooFar";
import PromptClockInAfterHourModal, {
  PromptClockInAfterHourProps,
} from "./PromptClockInAfterHour/PromptClockInAfterHourModal";
import PromptClockReadyMedflytAgencyModal, {
  PromptClockReadyMedflytAgencyModalProps,
} from "./PromptClockReadyMedflytAgencyModal/PromptClockReadyMedflytAgencyModal";
import PromptClockReadyModal, {
  PromptClockReadyModalProps,
} from "./PromptClockReadyModal/PromptClockReadyModal";
import PromptClockRejectedModal, {
  PromptClockRejectedModalProps,
} from "./PromptClockRejectedModal/PromptClockRejectedModal";
import PromptClockVerifyLocationModal, {
  PromptClockVerifyLocationModalProps,
} from "./PromptClockVerifyLocationModal/PromptClockVerifyLocationModal";
import PromptFutureVisitModal, { PromptFutureVisitModalProps } from "./PromptFutureVisitModal";
import PromptRequireSelfieModal, {
  PromptRequireSelfieModalProps,
} from "./PromptRequireSelfieModal/PromptRequireSelfieModal";
import PromptVisitActionSuccessModal, {
  PromptVisitActionSuccessModalProps,
} from "./PromptVisitActionSuccessModal/PromptVisitActionSuccessModal";
import { getApiErrorMessage } from "./Timesheet/TimesheetActivity";
import WatchLocationModal from "./WatchLocationModal";
import { UseContactSupportReturnType } from "./useContactSupport";
import useModal from "./useModal";
import PromptMissingFilesModal, {
  PromptMissingFilesModalProps,
} from "./PromptMissingFilesModal/PromptMissingFilesModal";
import {
  PromptVerifyMissedVisitModal,
  PromptVerifyMissedVisitModalProps,
} from "./PromptVerifyMissedVisitModal/PromptVerifyMissedVisitModal";

type ActiveModal =
  | "Error"
  | "Loading"
  | "Verify"
  | "Rejected"
  | "Ready"
  | "ReadyMedflyt"
  | "WhyNotToClock"
  | "TechnicalSupport"
  | "Success"
  | "FutureVisit"
  | "CaregiverLocationTooFar"
  | "ClockInAfterHour"
  | "PointsConfetti"
  | "WatchLocation"
  | "AdjustVisitTimes"
  | "Selfie"
  | "SelfieWithPatient"
  | "MissedDocuments"
  | "VerifyMissedVisit";

interface PromptClockParams {
  clockedInStatus: ClockedInStatus;
  visitInstance: VisitInstanceDetails;
  clockType: ClockInOutCallType;
}

interface UseClockInOutModalsProps {
  caregiverId: CaregiverId | Loading;
  isCaregiverOfMedflyt: boolean;
  onCheckIfDocumentsSubmitted: (visitInstanceId: VisitInstanceId) => Promise<boolean>;
  onRequireManualClock: (visitInstanceId: VisitInstanceId) => void;
  onClickClock: (
    visitInstanceId: VisitInstanceId,
    params: VisitInstanceClockinClockoutRequestParams,
    clockType: ClockInOutCallType
  ) => Promise<boolean>;
  onUploadVerificationSelfie: (
    visitInstanceId: VisitInstanceId,
    imageBase64: string,
    clockType: ClockInOutCallType
  ) => Promise<void>;
  onClockPresentVisit: (visitDetails: VisitInstanceDetails) => void;
  onModalClose?: () => void;
  onRequestVisitAdjustmentCheck: (
    visitInstanceId: VisitInstanceId
  ) => Promise<ClockinClockoutCheckAdjustmentStatusResponse>;
  onRequestMissedVisit: (visitInstanceId: VisitInstanceId) => Promise<void>;
}

const isClockInTimeInRange = (visitStartDateTime: LocalDateTime, clockType: ClockInOutCallType) => {
  return !(
    LocalDateTime.now().isAfter(visitStartDateTime.plusHours(1)) &&
    clockType === ClockInOutCallType.IN
  );
};

const isClockAddressIsInRange = (
  agencyCheckinMaxDistance: number | null,
  caregiverGeoLocation: Geo,
  visitGeoLocation: Geo
) => {
  if (agencyCheckinMaxDistance !== null) {
    const distance = getDistanceInFeet(caregiverGeoLocation, visitGeoLocation);
    if (distance > agencyCheckinMaxDistance) {
      return false;
    }
  }

  return true;
};

// The reason I'm not using discriminated unions is because I don't want the values
// of the modals to change while they transition to one another.
// The things we do for UX... 🤦‍♂️
const initialGeoAndAddress: GeoAndAddress = { address: "", geoLocation: { lat: 0, lng: 0 } };
const initialVisitInstanceId: VisitInstanceId = VisitInstanceId.wrap(-1);
const initialVisitDurationMinutes: number = 0;
const initialRequireSelfieOnClockIn: boolean = false;
const initialRequireSelfieOnClockOut: boolean = false;
const clockCarebackPoints: number = 20;

const validClockVisitTimeAdjustmentOffset = 7;

export function useClockInOutModals(props: UseClockInOutModalsProps) {
  const [activeModal, setActiveModal] = React.useState<ActiveModal | null>(null);
  const [showConfetti, setShowConfetti] = React.useState<boolean>(false);
  const [clockType, setClockType] = React.useState(ClockInOutCallType.IN);
  const [caregiverGeoAndAddress, setCaregiverGeoAndAddress] = React.useState(initialGeoAndAddress);
  const [visitInstanceId, setVisitInstanceId] = React.useState(initialVisitInstanceId);
  const [currentVisit, setCurrentVisit] = React.useState<VisitInstanceDetails | null>(null);
  const [visitDurationMinutes, setVisitDurationMinutes] = React.useState(
    initialVisitDurationMinutes
  );
  const [requireSelfieOnClockIn, setRequireSelfieOnClockIn] = React.useState(
    initialRequireSelfieOnClockIn
  );
  const [requireSelfieOnClockOut, setRequireSelfieOnClockOut] = React.useState(
    initialRequireSelfieOnClockOut
  );
  const [visitStartDateTime, setVisitStartDateTime] = React.useState<LocalDateTime>(
    LocalDateTime.now()
  );
  const [visitGeoLocation, setVisitGeoLocation] = React.useState<Geo>(
    initialGeoAndAddress.geoLocation
  );
  const [agencyCheckinMaxDistance, setAgencyCheckinMaxDistance] = React.useState<number | null>(
    null
  );
  const [isCalibrating, setIsCalibrating] = React.useState<boolean>(false);

  const [patientInitials, setPatientInitials] = React.useState<string>("");

  const [adjustmentProposal, setAdjustmentProposal] = React.useState<{
    adjustedUTC: Instant;
    adjustedLocal: LocalDateTime;
    offset: Duration;
  } | null>(null);

  const errorModal = useModal();
  const contactSupportModal = useModal();

  const setAllState = (params: PromptClockParams) => {
    setRequireSelfieOnClockIn(params.visitInstance.requireSelfieOnClockIn);
    setRequireSelfieOnClockOut(params.visitInstance.requireSelfieOnClockOut);
    setClockType(params.clockType);
    setVisitInstanceId(params.visitInstance.id);
    setVisitDurationMinutes(
      Duration.between(
        params.visitInstance.startTimeLocal,
        params.visitInstance.endTimeLocal
      ).toMinutes()
    );
    setCurrentVisit(params.visitInstance);
    setVisitGeoLocation(params.visitInstance.publicVisitDetails.location.location);

    if (params.visitInstance.checkinMaxDistance !== null) {
      setAgencyCheckinMaxDistance(params.visitInstance.checkinMaxDistance);
    }

    setPatientInitials(
      formatPatientInitials(params.visitInstance.publicVisitDetails.patientInitials)
    );
    setVisitStartDateTime(params.visitInstance.startTimeLocal);
  };

  const promptClock = async (params: PromptClockParams) => {
    setAllState(params);
    console.log(`Started Clock process type: ${params.clockType}`);
    const clockStrategy = getClockStrategy(params);

    console.log("Clock Strategy: ", clockStrategy);

    if (clockStrategy.visitType === "Present") {
      console.log("PRESENT VISIT");
    }

    if (!clockStrategy.valid) {
      setActiveModal("Error");
      errorModal.setModalAsError(clockStrategy);
      return;
    }

    if (
      params.clockType === ClockInOutCallType.OUT &&
      params.visitInstance.publicVisitDetails.agency.isMedflytAgency
    ) {
      setActiveModal("Loading");
      const allDocumentsSubmitted = await props.onCheckIfDocumentsSubmitted(
        params.visitInstance.id
      );
      if (!allDocumentsSubmitted) {
        console.log("Not all documents submitted");
        setActiveModal("MissedDocuments");
        return;
      }
    }
    switch (clockStrategy.strategy) {
      case "Timesheet":
      case "ClockedNotApproved":
        console.log("Manual Clock");
        props.onRequireManualClock(params.visitInstance.id);
        break;
      case "GeoLocation":
        await onRequireGeoLocationClock(params);
        break;
      default:
        assertNever(clockStrategy.strategy);
    }
  };

  const onRequireGeoLocationClock = async (params: PromptClockParams) => {
    console.log("Started GeoLocation Clock");
    setActiveModal("Loading");

    console.log("Getting Location Permission State");
    const locationPermissionState = await locationUtils.getLocationPermissionState();
    console.log("After getting Location Permission State", locationPermissionState);

    FirebaseAnalytics.logEvent({
      name: "[ClockInOut] location_permission_state",
      params: { state: locationPermissionState },
    });

    switch (locationPermissionState) {
      case "prompt":
      case "prompt-with-rationale":
      case "prompt_manually":
        setActiveModal("Verify");
        break;
      case "unknown":
      case "granted":
        try {
          const currentLocation = await locationUtils.getGeoAndAddress();
          setCaregiverGeoAndAddress(currentLocation);

          if (props.isCaregiverOfMedflyt) {
            const isInRange = isClockAddressIsInRange(
              params.visitInstance.checkinMaxDistance,
              currentLocation.geoLocation,
              visitGeoLocation
            );

            return isInRange ? setActiveModal("ReadyMedflyt") : setActiveModal("WatchLocation");
          }

          setActiveModal("Ready");
        } catch (e) {
          if (/location disabled/i.test(e.message)) {
            setActiveModal("Error");
            errorModal.setModalAsError({
              title: translate(STRINGS.ENABLE_LOCATION_TITLE),
              message: translate(STRINGS.ENABLE_LOCATION_MESSAGE),
            });
          } else {
            setActiveModal("Rejected");
          }
        }
        break;
      case "denied":
        setActiveModal("Rejected");
        break;
      default:
        assertNever(locationPermissionState);
    }
  };

  const handleClickClock = async () => {
    console.log("User chose to clock");
    FirebaseAnalytics.logEvent({
      name: "[ClockInOut] location_permission_state",
      params: { clockType },
    });

    if (
      !isClockAddressIsInRange(
        agencyCheckinMaxDistance,
        caregiverGeoAndAddress.geoLocation,
        visitGeoLocation
      )
    ) {
      console.log("Location not in range");
      setActiveModal("CaregiverLocationTooFar");
      return;
    }

    await handleClockInOutWithTimeCheck();
  };

  const handleClickClockMfCaregiver = async () => {
    console.log("[clock-modal] requested to clock");
    FirebaseAnalytics.logEvent({
      name: "[ClockInOut] location_permission_state",
      params: { clockType },
    });

    handleClockInOutSelfieIfNeeded();
  };

  const handleClockInOutWithTimeCheck = async () => {
    console.log("Time check");
    if (!isClockInTimeInRange(visitStartDateTime, clockType)) {
      console.log("Tiem not in range");
      setActiveModal("ClockInAfterHour");
      return;
    }

    await handleClockInOutSelfieIfNeeded();
  };

  const handleClockInOutSelfieIfNeeded = async () => {
    const isInRange = isClockAddressIsInRange(
      agencyCheckinMaxDistance,
      caregiverGeoAndAddress.geoLocation,
      visitGeoLocation
    );

    const isOutOfRangeForMedflytVisit =
      !isInRange && currentVisit?.publicVisitDetails.agency.isMedflytAgency === true;

    const isRequiredFromServer =
      clockType === ClockInOutCallType.IN ? requireSelfieOnClockIn : requireSelfieOnClockOut;

    if (isRequiredFromServer || isOutOfRangeForMedflytVisit) {
      setActiveModal(isOutOfRangeForMedflytVisit ? "SelfieWithPatient" : "Selfie");
      return;
    }

    await handleAdjustClockIfNeeded();
  };

  const getClockOffsetInMinutes = (visitInstance: VisitInstanceDetails) => {
    switch (clockType) {
      case ClockInOutCallType.IN:
        return Duration.between(visitInstance.startTimeUTC, Instant.now()).toMinutes();
      case ClockInOutCallType.OUT:
        return Duration.between(visitInstance.endTimeUTC, Instant.now()).toMinutes();
    }
  };

  const handleAdjustClockIfNeeded = async () => {
    if (currentVisit === null || !currentVisit.publicVisitDetails.agency.isMedflytAgency) {
      handleClockInOut({ adjustVisitTimes: false });
      return;
    }

    const clockOffsetInMinutes = getClockOffsetInMinutes(currentVisit);

    if (activeModal !== "Loading") {
      setActiveModal("Loading");
    }

    if (
      Math.abs(clockOffsetInMinutes) > validClockVisitTimeAdjustmentOffset &&
      clockType === ClockInOutCallType.IN
    ) {
      const status = await props.onRequestVisitAdjustmentCheck(visitInstanceId);

      if (status.result.status === "SUCCESS") {
        setActiveModal("AdjustVisitTimes");
        setAdjustmentProposal(status.result);
        return;
      }
    }

    handleClockInOut({ adjustVisitTimes: false });
  };

  const handleClockAfterAdjustPrompt = (params: { adjust: boolean }) => {
    handleClockInOut({ adjustVisitTimes: params.adjust });
  };

  const handleClockInOut = async (params: { adjustVisitTimes: boolean }) => {
    console.log("Clock in/out process");
    if (activeModal !== "Loading") {
      setActiveModal("Loading");
    }

    try {
      await props.onClickClock(
        visitInstanceId,
        {
          geoLocation: caregiverGeoAndAddress.geoLocation,
          adjustVisitTimes: params.adjustVisitTimes,
        },
        clockType
      );
    } catch (error) {
      showErrorModalFromApi(error);
      return;
    }

    setActiveModal("Success");
  };

  const handleClickClose = () => {
    setActiveModal(null);
    props.onModalClose?.();
    if (activeModal === "Success") {
      setShowConfetti(true);
    }
  };

  const handleCloseConfetti = () => {
    setShowConfetti(false);
  };

  const handleClickAllowAccess = async () => {
    console.log("User clicked on allow location access");
    FirebaseAnalytics.logEvent({
      name: "[ClockInOut] click allow location acccess",
      params: {},
    });

    try {
      setActiveModal("Loading");
      const result = await locationUtils.getGeoAndAdressOrNull();
      console.log("After get location", result);

      if (result === null) {
        console.log("User refused location access");
        FirebaseAnalytics.logEvent({
          name: "[ClockInOut] refused location after clicked allow location access",
          params: {},
        });
        setActiveModal("Rejected");
        return;
      }

      console.log("user allowed location access");

      FirebaseAnalytics.logEvent({
        name: "[ClockInOut] approved location after clicked allow location access",
        params: {},
      });

      setCaregiverGeoAndAddress(result);
      setActiveModal(props.isCaregiverOfMedflyt ? "ReadyMedflyt" : "Ready");
    } catch (e) {
      console.error(e);
      setActiveModal("Rejected");
    }
  };

  const handleClickDontWantToClock = () => {
    console.log("User clicked on don't want to clock");
    FirebaseAnalytics.logEvent({
      name: "[ClockInOut] click don't want to clock",
      params: {},
    });

    removeLocalStorageItem(HAS_PROMPTED_WITH_LOCATION_ADDRESS);
    handleClickClose();
  };

  const handleRequestCloseVerify = () => {
    removeLocalStorageItem(HAS_PROMPTED_WITH_LOCATION_ADDRESS);
    handleClickClose();
  };

  const handleRequestTechnicalSupport = () => {
    FirebaseAnalytics.logEvent({
      name: "[ClockInOut] click request technical support",
      params: {},
    });

    setActiveModal("TechnicalSupport");
  };

  const openFutureVisitModal = (visitDetails: VisitInstanceDetails) => {
    setVisitStartDateTime(visitDetails.startTimeLocal);
    setActiveModal("FutureVisit");
  };

  const handleRequestCalibrate = async () => {
    console.log("[clock-modal] requested to calibrate");
    setIsCalibrating(true);
    setCaregiverGeoAndAddress(await locationUtils.getGeoAndAddress());
    setIsCalibrating(false);
  };

  const loadingModalProps = {
    isOpen: activeModal === "Loading",
  };

  const verifyModalProps: PromptClockVerifyLocationModalProps = {
    isOpen: activeModal === "Verify",
    onClickAllowAccess: handleClickAllowAccess,
    onClickDontWantToClock: handleClickDontWantToClock,
    onRequestClose: handleRequestCloseVerify,
  };

  const handleClockPatientRefusedVisit = () => {
    setActiveModal("VerifyMissedVisit");
  };

  const readyModalProps: PromptClockReadyModalProps = {
    isOpen: activeModal === "Ready",
    clockType: clockType,
    geoAndAddress: caregiverGeoAndAddress,
    onClickClock: handleClickClock,
    onRequestClose: handleClickClose,
  };

  const watchLocationModalProps: React.ComponentProps<typeof WatchLocationModal> = {
    isOpen: activeModal === "WatchLocation",
    onError: (e) => {
      console.error("[watch-location]: error", e);
    },
    onSuccess: async (position) => {
      console.log("[watch-location]: success", position.coords);
      const newGeoAndAddress = await getGeoAndAddressByPosition(position);
      const caregiverGeoLocation: Geo = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };

      if (newGeoAndAddress) {
        setCaregiverGeoAndAddress(newGeoAndAddress);
      }

      const isInRange = isClockAddressIsInRange(
        agencyCheckinMaxDistance,
        caregiverGeoLocation,
        visitGeoLocation
      );

      console.log("[watch-location]: result", {
        isInRange,
        caregiverGeoLocation,
        visitGeoLocation,
      });

      if (isInRange) {
        setActiveModal("ReadyMedflyt");
      }
    },
    onTimeout: () => {
      console.log("[watch-location]: timeout");
      setActiveModal("ReadyMedflyt");
    },
    onRequestClose: handleClickClose,
  };

  const clockReadyForMedflytAgencyModalProps: PromptClockReadyMedflytAgencyModalProps = {
    clockAddressInRange: isClockAddressIsInRange(
      agencyCheckinMaxDistance,
      caregiverGeoAndAddress.geoLocation,
      visitGeoLocation
    ),
    clockInTimeInRange: isClockInTimeInRange(visitStartDateTime, clockType),
    isOpen: activeModal === "ReadyMedflyt",
    clockType: clockType,
    geoAndAddress: caregiverGeoAndAddress,
    isCalibrating: isCalibrating,
    onClickClock: handleClickClockMfCaregiver,
    onRequestCalibrate: handleRequestCalibrate,
    onRequestClose: handleClickClose,
  };

  const rejectedModalProps: PromptClockRejectedModalProps = {
    isOpen: activeModal === "Rejected",
    onRequestTechnicalSupport: handleRequestTechnicalSupport,
    onRequestClose: handleClickClose,
  };

  const successModalProps: PromptVisitActionSuccessModalProps = {
    isOpen: activeModal === "Success",
    visitActionType: clockType,
    visitDurationMinutes: visitDurationMinutes,
    adjustedVisitStartTime: adjustmentProposal?.adjustedLocal ?? null,
    onRequestClose: handleClickClose,
  };

  const futureVisitModalProps: PromptFutureVisitModalProps = {
    isOpen: activeModal === "FutureVisit",
    visitStartDateTime: visitStartDateTime,
    onRequestClose: handleClickClose,
  };

  const clockTooFarModalProps: PromptCaregiverLocationTooFarProps = {
    isOpen: activeModal === "CaregiverLocationTooFar",
    caregiverLocation: caregiverGeoAndAddress.geoLocation,
    visitLocation: visitGeoLocation,
    isClockIn: clockType === ClockInOutCallType.IN,
    onRequestClose: handleClickClose,
    onClockInOut: handleClockInOutWithTimeCheck,
    patientInitials: patientInitials,
  };

  const clockInAfterHourModalProps: PromptClockInAfterHourProps = {
    isOpen: activeModal === "ClockInAfterHour",
    onRequestClose: handleClickClose,
    onRequestClockIn: handleClockInOutSelfieIfNeeded,
  };

  const pointsConfettiProps = {
    isOpen: showConfetti,
    onClose: handleCloseConfetti,
    pointsNumber: clockCarebackPoints,
  };

  const showErrorModalFromApi = (error: unknown) => {
    const message = getApiErrorMessage({
      error,
      fallback: translate(
        clockType === ClockInOutCallType.IN ? STRINGS.CANT_CLOCK_IN : STRINGS.CANT_CLOCK_OUT
      ),
    });

    setActiveModal("Error");
    errorModal.setModalAsError({
      title: translate(STRINGS.SOMETHING_WENT_WRONG),
      message: message,
    });
  };

  const handleClockWithSelfie = async (imageBase64: string) => {
    console.log("started clock in with selfie");
    if (activeModal !== "Loading") {
      setActiveModal("Loading");
    }
    try {
      await props.onUploadVerificationSelfie(visitInstanceId, imageBase64, clockType);
    } catch (error) {
      console.log("Error uploading selfie: ", error);
    }
    await handleAdjustClockIfNeeded();
  };

  const handleClickConfirmMissedVisit = async () => {
    setActiveModal("Loading");
    try {
      await props.onRequestMissedVisit(visitInstanceId);
    } catch (error) {
      showErrorModalFromApi(error);
      return;
    }
    setActiveModal(null);
    props.onModalClose?.();
  };

  const requireSelfieModalProps: PromptRequireSelfieModalProps = {
    isOpen: activeModal === "Selfie" || activeModal === "SelfieWithPatient",
    type: activeModal === "Selfie" ? "alone" : "with-patient",
    clockType: clockType,
    onSuccess: handleClockWithSelfie,
    onRequestClose: handleClickClose,
  };

  const missingDocumentsModalProps: PromptMissingFilesModalProps = {
    isOpen: activeModal === "MissedDocuments",
    onRequestClose: () => {
      setActiveModal(null);
      props.onModalClose?.();
    },
    onRequestPatientRefusedVisit: handleClockPatientRefusedVisit,
  };

  const verifyMissedVisitModalProps: PromptVerifyMissedVisitModalProps = {
    isOpen: activeModal === "VerifyMissedVisit",
    onClickClose: () => {
      setActiveModal(null);
      props.onModalClose?.();
    },
    onClickConfirm: handleClickConfirmMissedVisit,
  };

  const adjustVisitTimesModalProps = ((): PromptAdjustVisitTimesModalProps => {
    // This should not happen, but this component is a wreck in terms of context-awareness.
    if (adjustmentProposal === null || currentVisit === null) {
      return {
        isOpen: false,
        offset: Duration.ZERO,
        adjustedEndTimeUTC: currentVisit?.endTimeUTC ?? Instant.now(),
        onRequestClose: handleClickClock,
        onSuccess: handleClockAfterAdjustPrompt,
      };
    }

    return {
      isOpen: activeModal === "AdjustVisitTimes",
      offset: adjustmentProposal.offset,
      adjustedEndTimeUTC: currentVisit.endTimeUTC.plus(adjustmentProposal.offset),
      onRequestClose: handleClickClose,
      onSuccess: handleClockAfterAdjustPrompt,
    };
  })();

  return {
    state: {
      loadingModalProps: loadingModalProps,
      verifyModalProps: verifyModalProps,
      readyModalProps: readyModalProps,
      clockReadyMedflytModalProps: clockReadyForMedflytAgencyModalProps,
      watchLocationModalProps: watchLocationModalProps,
      rejectedModalProps: rejectedModalProps,
      successModalProps: successModalProps,
      futureVisitModalProps: futureVisitModalProps,
      clockTooFarModalProps: clockTooFarModalProps,
      clockInAfterHourModalProps: clockInAfterHourModalProps,
      pointsConfettiProps: pointsConfettiProps,
      errorModalProps: {
        ...errorModal.modalProps,
        isOpen: activeModal === "Error",
        onRequestClose: handleClickClose,
        actions: [
          {
            key: "close",
            children: translate(STRINGS.CLOSE),
            onClick: handleClickClose,
            variant: "soft" as const,
            color: "danger" as const,
          },
        ],
      },
      contactSupportModalProps: {
        ...contactSupportModal.modalProps,
        isOpen: activeModal === "TechnicalSupport",
        onRequestClose: handleClickClose,
      },
      requireSelfieModalProps: requireSelfieModalProps,
      missingFilesModalProps: missingDocumentsModalProps,
      verifyMissedVisitModalProps: verifyMissedVisitModalProps,
      adjustVisitTimesModalProps: adjustVisitTimesModalProps,
    },
    actions: { promptClock, openFutureVisitModal },
  };
}

const getClockStrategy = (params: PromptClockParams) => {
  switch (params.clockType) {
    case ClockInOutCallType.IN:
      return getClockInStrategy(params.visitInstance);
    case ClockInOutCallType.OUT:
      return getClockOutStrategy(params.visitInstance);
    default:
      assertNever(params.clockType);
  }
};

type UseClockInOutModalPropsState = ReturnType<typeof useClockInOutModals>;

export type ClockInOutModalsProps = UseClockInOutModalPropsState["state"] & {
  contactSupportProps: UseContactSupportReturnType;
};

function ClockInOutModals(props: ClockInOutModalsProps) {
  return (
    <>
      <LoadingModal {...props.loadingModalProps} />
      <PromptClockVerifyLocationModal {...props.verifyModalProps} />
      <PromptClockReadyModal {...props.readyModalProps} />
      <PromptClockReadyMedflytAgencyModal {...props.clockReadyMedflytModalProps} />
      <PromptClockRejectedModal {...props.rejectedModalProps} />
      <ErrorModal {...props.errorModalProps} />
      <ContactSupportModal {...props.contactSupportProps} {...props.contactSupportModalProps} />
      <PromptVisitActionSuccessModal {...props.successModalProps} />
      <PromptFutureVisitModal {...props.futureVisitModalProps} />
      <WatchLocationModal {...props.watchLocationModalProps} />
      <PromptCaregiverLocationTooFarModal {...props.clockTooFarModalProps} />
      <PromptClockInAfterHourModal {...props.clockInAfterHourModalProps} />
      <PointsConfetti {...props.pointsConfettiProps}></PointsConfetti>
      <PromptRequireSelfieModal {...props.requireSelfieModalProps} />
      <PromptAdjustVisitTimesModal {...props.adjustVisitTimesModalProps} />
      <PromptMissingFilesModal {...props.missingFilesModalProps} />
      <PromptVerifyMissedVisitModal {...props.verifyMissedVisitModalProps} />
    </>
  );
}

export default ClockInOutModals;
